import { useEffect, useRef, useState } from "preact/hooks";
import type { Source } from "@fab-judge/schema";
import { fetchRuleText, getCachedRuleText } from "../api/rules";

/** Popup dimensions, kept in sync with the CSS (`min(22rem, 100vw - 16px)`). */
const PANEL_WIDTH = 352;
const EDGE = 8;

/**
 * Inline rule citation (e.g. `1.3.2a`) that opens a small popup with the rule's
 * text, fetched from the public `rule_sections` table (cached per session).
 *
 * The popup is absolutely positioned and clamped to the viewport so it never
 * spills off-screen.
 */
export function RuleRef({
  ruleRef,
  source,
}: {
  ruleRef: string;
  source?: Source;
}) {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [left, setLeft] = useState<number | null>(null);
  const [above, setAbove] = useState(false);
  const [text, setText] = useState<string | null>(() =>
    getCachedRuleText(ruleRef)
  );
  const [error, setError] = useState<string | null>(null);

  function reposition() {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const rect = wrapper.getBoundingClientRect();
    const width = Math.min(PANEL_WIDTH, window.innerWidth - EDGE * 2);
    const desired = rect.left + rect.width / 2 - width / 2;
    const clamped = Math.max(
      EDGE,
      Math.min(desired, window.innerWidth - width - EDGE),
    );
    setLeft(clamped - rect.left);

    // Flip above the citation when there is not enough room below.
    const spaceBelow = window.innerHeight - rect.bottom;
    setAbove(spaceBelow < 260 && rect.top > spaceBelow);
  }

  function toggle() {
    if (open) {
      setOpen(false);
      return;
    }

    if (text === null && error === null) {
      const cached = getCachedRuleText(ruleRef);
      if (cached !== null) {
        setText(cached);
      } else {
        fetchRuleText(ruleRef)
          .then((value) => setText(value))
          .catch((cause: unknown) =>
            setError(
              cause instanceof Error ? cause.message : "Could not load rule.",
            )
          );
      }
    }
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;

    reposition();
    const onResize = () => reposition();
    function onPointerDown(event: MouseEvent) {
      const wrapper = wrapperRef.current;
      if (wrapper && !wrapper.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    window.addEventListener("resize", onResize);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onResize);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span class="rule-ref" ref={wrapperRef}>
      <button
        type="button"
        class="rule-ref__btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
      >
        {ruleRef}
      </button>

      {open && (
        <span
          class="rule-ref__panel"
          role="dialog"
          aria-label={`Comprehensive Rules ${ruleRef}`}
          style={{
            width: `min(22rem, calc(100vw - ${EDGE * 2}px))`,
            left: `${left ?? 0}px`,
            top: above ? "auto" : "calc(100% + 6px)",
            bottom: above ? "calc(100% + 6px)" : "auto",
            visibility: left === null ? "hidden" : "visible",
          }}
        >
          <span class="rule-ref__title">Comprehensive Rules {ruleRef}</span>

          {text !== null
            ? (
              <span class="rule-ref__text">
                {text.length > 0 ? text : "No text found for this rule."}
              </span>
            )
            : error !== null
            ? (
              <span class="rule-ref__error" role="alert">
                {error}
              </span>
            )
            : <span class="rule-ref__text">Loading…</span>}

          {source?.url && (
            <a
              class="rule-ref__link"
              href={source.url}
              target="_blank"
              rel="noreferrer"
            >
              Official rules ↗
            </a>
          )}
        </span>
      )}
    </span>
  );
}
